import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  EntityHeader, TabBar, Button, Select, Textarea, SectionCard, useToast, surface,
} from '@datahub/design-system'
import { tabsForCycle, tabsForForm, type TabDef, type PatchOperation } from '@datahub/shared'
import { get, post, setPortalToken } from '../lib/api'
import { track } from '../lib/analytics'
import type { PortalContext, ValidationIssue } from '../lib/types'
import { useDraft } from './useDraft'
import { PortalTopBar } from './PortalTopBar'
import { Landing } from './Landing'
import { PortalSidebar } from './PortalSidebar'
import { PortalPage } from './PortalPage'
import { RequirementsPanel } from './RequirementsPanel'
import { NoOpenRequest } from './NoOpenRequest'
import { SectionRenderer } from './form/SectionRenderer'
import { DraftContext } from './form/draft-context'
import { DocumentsTab } from './DocumentsTab'
import { ReviewTab } from './ReviewTab'
import { StatusPage } from './StatusPage'
import { closedMessage, offersSelfServeStart, portalPhaseFor, type Phase } from './phase'

export default function PortalApp() {
  const { token } = useParams<{ token: string }>()
  const toast = useToast()

  const [phase, setPhase] = useState<Phase>('loading')
  const [context, setContext] = useState<PortalContext | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('general')
  const [issues, setIssues] = useState<ValidationIssue[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submittedRef, setSubmittedRef] = useState<string | null>(null)
  const [uploadSignal, setUploadSignal] = useState(0)
  const [startingSelfServe, setStartingSelfServe] = useState(false)

  useEffect(() => { setPortalToken(token ?? null) }, [token])

  const loadContext = useCallback(async () => {
    try {
      const ctx = await get<PortalContext>('/portal/context')
      setContext(ctx)
      track('datahub_link_opened')
      // Without an open cycle the server refuses every draft call, so the portal must
      // not offer the form.
      setPhase(portalPhaseFor(ctx))
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'This link could not be opened')
      setPhase('error')
    }
  }, [])

  useEffect(() => { if (token) void loadContext() }, [token, loadContext])

  const formActive = phase === 'form'
  // Which form this entity fills in — the company profile, a fund manager's or a limited
  // partner's — comes from the server with the rest of the context.
  const form = context?.form ?? 'company'
  const requestedTabs = useMemo(() => context?.cycle?.requestedTabs ?? [], [context])
  const draft = useDraft(formActive, form, requestedTabs)

  /** Tabs this cycle asks for; an ad-hoc request may narrow the set (CYC-09). */
  const visibleTabs: TabDef[] = useMemo(
    () => tabsForCycle(form, requestedTabs),
    [form, requestedTabs],
  )

  const tab = visibleTabs.find((t) => t.key === activeTab) ?? visibleTabs[0]!
  const state = draft.payload?.tabs[tab.key] ?? { values: {}, rows: {}, controls: {}, flags: {}, note: '' }

  const profile = useMemo(() => {
    const general = draft.payload?.tabs['general']
    if (!general) return {}
    return Object.fromEntries(
      Object.entries(general.values).map(([k, v]) => [k.includes('.') ? k.slice(k.indexOf('.') + 1) : k, v]),
    )
  }, [draft.payload])

  const draftContext = useMemo(
    () => ({ payload: draft.payload ?? undefined, tabs: tabsForForm(form) }),
    [draft.payload, form],
  )

  const refreshIssues = useCallback(async () => {
    try {
      const res = await get<{ issues: ValidationIssue[] }>('/portal/validate')
      setIssues(res.issues)
    } catch {
      // Validation is advisory here; the server re-checks on submit.
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'review' && formActive) void refreshIssues()
  }, [activeTab, formActive, refreshIssues])

  const goToTab = async (next: string) => {
    await draft.flush()
    track('datahub_tab_viewed', { tab: next, completionPct: draft.tabProgress[next]?.pct })
    setActiveTab(next)
    document.querySelector('#portal-body')?.scrollTo({ top: 0 })
  }

  const submit = async (input: { submittedByName: string; submittedByEmail: string; submittedByContactId: string | null }) => {
    setSubmitting(true)
    try {
      await draft.flush()
      const res = await post<{ reference: string }>('/portal/submit', { ...input, declarationAccepted: true })
      setSubmittedRef(res.reference)
      track('datahub_submitted', { completionPct: draft.overallPct })
      toast.success('Thank you. Your update has been sent to Stears.', 'Submitted')
      await loadContext()
      setPhase('submitted')
    } catch (err) {
      const details = (err as { error?: { details?: { issues?: ValidationIssue[] } } }).error?.details
      if (details?.issues) {
        setIssues(details.issues)
        toast.error('Some answers still need attention before you can submit')
      } else {
        toast.error(err instanceof Error ? err.message : 'That submission did not go through')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (phase === 'loading') {
    return (
      <PortalPage>
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100% - 44px)', padding: 48 }}>
          <span className="font-mulish text-text-support" style={{ fontSize: 12 }}>Loading…</span>
        </div>
      </PortalPage>
    )
  }

  if (phase === 'error' || !context) {
    return (
      <PortalPage>
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100% - 44px)', padding: 48 }}>
          <div style={{ maxWidth: 460 }}>
            <h1 className="font-jakarta font-bold text-white" style={{ fontSize: 20, marginBottom: 12 }}>This link is not working</h1>
            <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px' }}>
              {errorMessage ?? 'Contact the Stears data team and we will send you a new one.'}
            </p>
          </div>
        </div>
      </PortalPage>
    )
  }

  // CYC-10: the company does not have to wait to be asked. The closed page offers it too.
  const startSelfServe = async () => {
    setStartingSelfServe(true)
    try {
      await post('/portal/self-serve')
      await loadContext()
      await draft.reload()
      setPhase('form')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start an update')
    } finally {
      setStartingSelfServe(false)
    }
  }

  if (phase === 'no_cycle') {
    return <NoOpenRequest context={context} starting={startingSelfServe} onStart={startSelfServe} />
  }

  if (phase === 'closed' && context.cycle?.closedOn) {
    return (
      <PortalPage>
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100% - 44px)', padding: 48 }}>
          <div style={{ maxWidth: 460 }}>
            <h1 className="font-jakarta font-bold text-white" style={{ fontSize: 20, marginBottom: 12 }}>
              {context.cycle.periodLabel}
            </h1>
            <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px' }}>
              {closedMessage(context.cycle.closedOn)}
            </p>
            {offersSelfServeStart(phase) && (
              <div style={{ marginTop: 24 }}>
                <Button variant="filled-grey" size="medium" onClick={startSelfServe} disabled={startingSelfServe} className="w-full">
                  {startingSelfServe ? 'Opening…' : 'Start an update'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </PortalPage>
    )
  }

  if (phase === 'landing') {
    return <Landing context={context} onStart={() => setPhase('form')} />
  }

  if (phase === 'submitted') {
    return (
      <StatusPage
        context={context}
        reference={submittedRef ?? context.submission?.reference ?? ''}
        closedOn={context.cycle?.closedOn ?? null}
        onAmend={async () => { await post('/portal/amend'); await draft.reload(); setActiveTab('general'); setPhase('form') }}
      />
    )
  }

  const contextLine = `${context.company.displayName} · ${context.cycle?.periodLabel ?? ''}${context.cycle?.deadlineLabel ? ` · due ${context.cycle.deadlineLabel}` : ''}`
  const tabLabels = visibleTabs.map((t) => `${t.shortLabel}${draft.tabProgress[t.key] ? ` · ${draft.tabProgress[t.key]!.pct}%` : ''}`)
  const activeLabel = tabLabels[visibleTabs.findIndex((t) => t.key === tab.key)]!
  const index = visibleTabs.findIndex((t) => t.key === tab.key)

  return (
    <>
      <PortalTopBar
        contextLine={contextLine}
        companyName={context.company.displayName}
        saveState={draft.saveState}
        saveError={draft.error}
        onSave={() => void draft.flush()}
        onUpload={() => void goToTab('documents')}
      />
      <DraftContext.Provider value={draftContext}>
      <main
        style={{ marginTop: 80, height: 'calc(100vh - 80px)', overflow: 'hidden', backgroundColor: surface.page }}
        className="flex"
      >
        {/* Figma node 4352:18988 puts the sections in a 256px sidebar beside the form. */}
        <div className="hidden lg:flex">
          <PortalSidebar
            section="Datahub reporting"
            items={visibleTabs.map((t, i) => ({ key: t.key, label: tabLabels[i]! }))}
            active={tab.key}
            onSelect={(key) => void goToTab(key)}
          />
        </div>

        <div className="flex flex-col flex-1 min-w-0" style={{ padding: 20, height: '100%' }}>
          <EntityHeader
            name={context.company.displayName}
            type="Datahub profile update"
            rightSlot={
              <span className="font-jakarta text-white" style={{ fontSize: 12 }}>
                Overall {draft.overallPct}% complete
              </span>
            }
          />

          {tab.key === 'documents' && (
            <div style={{ marginTop: 20 }}>
              <RequirementsPanel
                companyName={context.company.displayName}
                periodLabel={context.cycle?.periodLabel ?? 'Update'}
              />
            </div>
          )}

          <div className="hidden md:block lg:hidden">
            <TabBar
              tabs={tabLabels}
              active={activeLabel}
              onChange={(label) => void goToTab(visibleTabs[tabLabels.indexOf(label)]!.key)}
            />
          </div>
          <div className="md:hidden bg-background-main" style={{ padding: 12, borderLeft: '0.5px solid #4B5563', borderRight: '0.5px solid #4B5563', borderBottom: '0.5px solid #4B5563' }}>
            <Select
              size="large"
              ariaLabel="Choose a tab"
              options={visibleTabs.map((t, i) => ({ value: t.key, label: tabLabels[i]! }))}
              value={tab.key}
              onChange={(v) => void goToTab(v)}
            />
          </div>

          {/* 2px progress line under the title bar (plan Section 12.7). */}
          <div style={{ height: 2, backgroundColor: '#2D3036' }}>
            <div style={{ height: 2, width: `${draft.overallPct}%`, backgroundColor: '#FFFFFF', transition: 'width 0.2s ease' }} />
          </div>

          <div
            id="portal-body"
            className="flex-1 overflow-y-auto bg-background-main"
            style={{ padding: 20, borderLeft: '0.5px solid #4B5563', borderRight: '0.5px solid #4B5563', borderBottom: '0.5px solid #4B5563' }}
          >
            <div className="flex flex-col" style={{ gap: 20 }}>
              {draft.concurrentTab === tab.key && (
                <div className="font-mulish text-white" style={{ fontSize: 12, padding: '10px 16px', backgroundColor: '#2D3036', border: '0.5px solid #4B5563' }}>
                  Someone else is editing this tab. The most recent change to each field is kept.
                </div>
              )}
              {tab.intro && (
                <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px' }}>{tab.intro}</p>
              )}

              {tab.key === 'review' ? (
                <ReviewTab
                  context={context}
                  tabProgress={draft.tabProgress}
                  issues={issues}
                  submitting={submitting}
                  onEditTab={(t) => void goToTab(t)}
                  onSubmit={submit}
                />
              ) : tab.key === 'documents' ? (
                <DocumentsTab
                  toggled={Boolean(state.flags['nothing_to_add'])}
                  onToggle={(v) => draft.push({ op: 'flag', tab: tab.key, key: 'nothing_to_add', value: v } as PatchOperation)}
                  push={draft.push}
                  reloadSignal={uploadSignal}
                />
              ) : (
                <>
                  {tab.sectionControls?.map((control) => (
                    <SectionCard key={control.key} title="Section settings">
                      <div style={{ padding: '12px 20px' }}>
                        <label className="flex items-center font-jakarta text-white" style={{ gap: 10, fontSize: 12, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={Boolean(state.controls[control.key])}
                            onChange={(e) => draft.push({ op: 'control', tab: tab.key, key: control.key, value: e.target.checked } as PatchOperation)}
                            style={{ accentColor: '#4B5563' }}
                          />
                          {control.label}
                        </label>
                      </div>
                    </SectionCard>
                  ))}

                  {tab.sections.map((section) => (
                    <SectionRenderer
                      key={section.key}
                      tab={tab.key}
                      section={section}
                      state={state}
                      profile={profile}
                      issues={issues.filter((i) => i.tab === tab.key)}
                      push={draft.push}
                    />
                  ))}

                  <SectionCard title="Notes and clarifications">
                    <div style={{ padding: '16px 20px' }}>
                      <Textarea
                        ariaLabel={`Notes and clarifications for ${tab.label}`}
                        value={state.note}
                        onChange={(v) => draft.push({ op: 'note', tab: tab.key, value: v } as PatchOperation)}
                        rows={3}
                        maxLength={4000}
                        help="Anything Stears should know about this section, including why you removed something."
                      />
                    </div>
                  </SectionCard>
                </>
              )}

              {tab.key !== 'review' && (
                <div className="flex items-center justify-end" style={{ gap: 10, paddingBottom: 20 }}>
                  {index > 0 && (
                    <Button variant="outline" size="medium" onClick={() => void goToTab(visibleTabs[index - 1]!.key)}>Back</Button>
                  )}
                  {/* Figma node 4352:18988: the forward action is the white button. */}
                  <Button variant="filled-white" size="medium" onClick={() => void goToTab(visibleTabs[Math.min(index + 1, visibleTabs.length - 1)]!.key)}>
                    Continue
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      </DraftContext.Provider>
      <span hidden onClick={() => setUploadSignal((n) => n + 1)} />
    </>
  )
}
